import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppData } from '../../contexts/AppDataContext';
import { useDocuments } from '../../contexts/DocumentsContext';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  ChartBarIcon,
  DocumentChartBarIcon,
  ChartPieIcon,
  CurrencyDollarIcon,
  PresentationChartLineIcon,
  MagnifyingGlassIcon,
  // REMOVED: ArrowDownTrayIcon - no longer used
  TruckIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  ChartBarSquareIcon,

  ScaleIcon
} from '@heroicons/react/24/outline';

import SalesReports from './SalesReports';
import InventoryReports from './InventoryReports';
import PurchasesReports from './PurchasesReports';
import FinancialReports from './FinancialReports';
import AbcClassificationReport from './AbcClassificationReport';
import ProfitabilityReport from './ProfitabilityReport';


const ReportsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('sales');
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { t } = useLanguage();
  
  // Check if we're on the main dashboard (no specific route)
  const isMainDashboard = location.pathname === '/reports' || location.pathname === '/reports/';
  
  // Check which section is currently active
  const isInventorySection = location.pathname.includes('/inventory');
  const isSalesSection = location.pathname.includes('/sales') || isMainDashboard;
  
  // Get real-time data from contexts
  const { 
    customers, 
    suppliers, 
    products, 
    sales: rawSales,
    purchases,
    fetchAllData 
  } = useAppData();
  
  // Transform sales data to match the format used in SalesPage
  const sales = useMemo(() => {
    if (!rawSales) return [];
    
    // Import the same transformation function used in SalesPage
    const { transformSalesData } = require('../../utils/salesDataUtils');
    const transformedSales = transformSalesData(rawSales, customers);
    
    console.log('🔄 ReportsPage: Transformed sales data', {
      rawCount: rawSales.length,
      transformedCount: transformedSales.length,
      sampleTransformed: transformedSales[0]
    });
    
    return transformedSales;
  }, [rawSales, customers]);
  
  const { 
    documents, 
    stats: documentStats, 
    fetchDocuments 
  } = useDocuments();

  // Load fresh data when component mounts - ONLY ONCE
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAllData();
        await fetchDocuments();
        // Wait a bit for state updates to propagate
        setTimeout(() => setIsDataLoaded(true), 500);
      } catch (error) {
        console.error('Error loading reports data:', error);
        setIsDataLoaded(true); // Show content even if errors
      }
    };
    loadData();
  }, []); // Empty dependency array to run only once on mount



  const handleTabClick = (tab) => {
    setActiveTab(tab);
    navigate(`/reports/${tab}`);
  };

  // Debug what data we have available (only when data loads)
  useEffect(() => {
    if (isDataLoaded) {
      console.log('📊 ReportsPage data loaded:', {
        documentsCount: documents?.length || 0,
        productsCount: products?.length || 0,
        customersCount: customers?.length || 0,
        suppliersCount: suppliers?.length || 0,
        documentSample: documents?.[0]?.type
      });
    }
  }, [isDataLoaded, documents?.length, products?.length, customers?.length, suppliers?.length]);

  // Calculate top products from real sales data
  const topProductsFromSales = useMemo(() => {
    if (!sales || sales.length === 0 || !products || products.length === 0) {
      console.log('⚠️ No sales or products data for top products calculation');
      return [];
    }

    console.log('📊 Calculating top products from sales data:', {
      salesCount: sales.length,
      productsCount: products.length
    });

    // Calculate product sales quantities
    const productSales = {};
    sales.forEach(sale => {
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const productId = item.id || item.productId;
          if (!productId) return;

          if (!productSales[productId]) {
            productSales[productId] = {
              quantity: 0,
              name: item.name || 'Produit inconnu'
            };
          }
          productSales[productId].quantity += parseInt(item.quantity) || 1;
        });
      }
    });

    // Convert to array and sort by quantity
    const topProducts = Object.entries(productSales)
      .map(([id, data]) => {
        const product = products.find(p => p.id === id);
        return {
          id,
          name: product?.name || data.name,
          quantity: data.quantity
        };
      })
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);

    console.log('🏆 Top products calculated:', topProducts);
    return topProducts;
  }, [sales, products]);

  // Calculate sales evolution data for chart (last 12 months)
  const salesEvolutionData = useMemo(() => {
    console.log('📈 Calculating sales evolution data:', {
      salesCount: sales?.length || 0,
      documentsCount: documents?.length || 0
    });

    console.log('📈 Calculating sales evolution data from:', sales);

    // Get last 12 months
    const months = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = monthDate.toLocaleDateString('fr-FR', { month: 'short' });
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      
      // Calculate sales for this month from all sources
      let monthSales = 0;
      
      // From sales context
      if (sales && sales.length > 0) {
        sales.forEach(sale => {
          const saleDate = new Date(sale.date || sale.createdAt || Date.now());
          if (saleDate.getFullYear() === year && saleDate.getMonth() === month) {
            // Calculate sale amount from items or total
            const amount = sale.items && Array.isArray(sale.items) 
              ? sale.items.reduce((sum, item) => sum + (parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0))), 0)
              : parseFloat(sale.total || 0);
            monthSales += amount;
          }
        });
      }
      
      // From documents context (invoices)
      if (documents && documents.length > 0) {
        documents.forEach(doc => {
          if (doc.type === 'invoice' && doc.status !== 'cancelled') {
            const docDate = new Date(doc.date);
            if (docDate.getFullYear() === year && docDate.getMonth() === month) {
              monthSales += doc.total || 0;
            }
          }
        });
      }
      
      months.push({
        month: monthName,
        sales: monthSales,
        year: year,
        monthIndex: month
      });
    }

    console.log('📈 Sales evolution calculated:', months);
    
    // If no sales data but we have structure, show empty chart
    const hasAnySales = months.some(m => m.sales > 0);
    if (!hasAnySales && (!sales || sales.length === 0) && (!documents || documents.length === 0)) {
      return []; // Show empty state
    }
    
    return months;
  }, [sales, documents]);

  // Calculate inventory overview data for inventory section
  const inventoryOverviewData = useMemo(() => {
    console.log('🔍 Building inventory overview data from products:', {
      productsCount: products?.length || 0,
      sampleProducts: products?.slice(0, 3),
      allProducts: products
    });
    if (!products || products.length === 0) {
      return {
        topValueProducts: [],
        lowStockProducts: [],
        stockByCategory: []
      };
    }

    console.log('📦 Calculating inventory overview for dashboard:', products.length, 'products');

    // Top products by stock value
    const topValueProducts = products
      .map(product => ({
        id: product.id,
        name: product.name || 'Produit inconnu',
        category: product.category || 'Non catégorisé',
        stock: product.stock || 0,
        value: (product.stock || 0) * (product.price || 0)
      }))
      .filter(product => product.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Low stock products
    const lowStockProducts = products
      .filter(product => {
        const currentStock = product.stock || 0;
        const minStock = product.minStock || 5;
        return currentStock <= minStock;
      })
      .map(product => ({
        id: product.id,
        name: product.name || 'Produit inconnu',
        currentStock: product.stock || 0,
        minStock: product.minStock || 5,
        priority: (product.stock || 0) === 0 ? 'Critique' : 
                 (product.stock || 0) <= 2 ? 'Urgent' : 'Normal'
      }))
      .sort((a, b) => a.currentStock - b.currentStock)
      .slice(0, 5);

    // Stock by category for trends
    const categoryData = {};
    products.forEach(product => {
      const category = product.category || 'Non catégorisé';
      const quantity = product.stock || 0;
      const value = quantity * (product.price || 0);

      if (!categoryData[category]) {
        categoryData[category] = { name: category, quantity: 0, value: 0, products: 0 };
      }

      categoryData[category].quantity += quantity;
      categoryData[category].value += value;
      categoryData[category].products += 1;
    });

    const stockByCategory = Object.values(categoryData)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6)
      .map((cat, index) => ({
        ...cat,
        month: cat.name.slice(0, 3), // Short name for chart
        stockValue: cat.value
      }));

    console.log('📦 Inventory overview calculated:', {
      topValueProducts: topValueProducts.length,
      lowStockProducts: lowStockProducts.length,
      stockByCategory: stockByCategory.length
    });

    return {
      topValueProducts,
      lowStockProducts,
      stockByCategory
    };
  }, [products]);

  // Calculate real-time statistics with memoization to prevent flashing
  const stats = useMemo(() => {
    console.log('📊 Calculating real-time dashboard indicators...');
    
    // 1. CHIFFRE D'AFFAIRES: Calculate from actual sales data
    let totalRevenue = 0;
    
    // Method 1: From sales data (primary source - most accurate)
    if (sales && sales.length > 0) {
      console.log('🔍 DEBUG SALES DATA:', sales.slice(0, 2));
      
      totalRevenue = sales.reduce((sum, sale) => {
        // Only count completed/paid sales
        if (sale.status === 'completed' || sale.status === 'paid' || sale.status === 'delivered') {
          // Try multiple possible locations for the total amount
          const saleAmount = sale.rawAmount || sale.total || 
                           (sale.__raw && sale.__raw.total ? parseFloat(sale.__raw.total) : 0);
          
          console.log(`📊 Sale ${sale.id || 'unknown'}: Status=${sale.status}, Amount=${saleAmount}`);
          return sum + saleAmount;
        }
        return sum;
      }, 0);
      console.log('💰 Revenue calculated from sales data:', totalRevenue);
    } 
    // Method 2: From invoice documents (fallback)
    else if (documents && documents.length > 0) {
      const paidInvoices = documents.filter(doc => 
        doc.type === 'invoice' && (doc.status === 'paid' || doc.status === 'delivered')
      ) || [];
      totalRevenue = paidInvoices.reduce((sum, doc) => sum + (parseFloat(doc.total) || 0), 0);
      console.log('💰 Revenue calculated from invoice documents:', totalRevenue);
    }

    // 2. VALEUR DU STOCK: Calculate real stock value using cost price (not selling price)
    let stockValue = 0;
    
    if (products && products.length > 0) {
      stockValue = products.reduce((sum, product) => {
        const quantity = parseInt(product.stock) || 0;
        // Use cost price for valuation (not selling price)
        const costPrice = parseFloat(product.cost) || parseFloat(product.costPrice) || 0;
        const itemValue = quantity * costPrice;
        
        console.log(`📦 Product ${product.name}: ${quantity} × ${costPrice} = ${itemValue}`);
        return sum + itemValue;
      }, 0);
      console.log('📊 Total stock value calculated:', stockValue);
    }

    // 3. RENTABILITÉ GLOBALE: Calculate real profitability based on sales profit vs. costs
    let profitability = 0;
    let totalCost = 0;
    let totalProfit = 0;
    
    // Method 1: Calculate from sales with product costs
    if (sales && sales.length > 0 && products && products.length > 0) {
      // Calculate total cost and profit from sales
      sales.forEach(sale => {
        if (sale.items && Array.isArray(sale.items)) {
          sale.items.forEach(item => {
            const productId = item.id || item.productId;
            const product = products.find(p => p.id === productId);
            
            if (product) {
              const quantity = parseInt(item.quantity) || 1;
              const revenue = parseFloat(item.total) || quantity * (parseFloat(item.price) || 0);
              const costPrice = parseFloat(product.cost) || parseFloat(product.costPrice) || 0;
              const cost = quantity * costPrice;
              
              totalCost += cost;
              totalProfit += (revenue - cost);
            }
          });
        }
      });
      
      // Calculate profitability as profit / cost
      profitability = totalCost > 0 ? (totalProfit / totalCost * 100) : 0;
      console.log('📈 Profitability calculated from sales and costs:', {
        totalRevenue,
        totalCost,
        totalProfit,
        profitability: profitability.toFixed(1) + '%'
      });
    } 
    // Method 2: Simplified calculation (fallback)
    else {
      profitability = stockValue > 0 ? ((totalRevenue - stockValue) / stockValue * 100) : 0;
      console.log('📉 Simplified profitability calculation:', profitability.toFixed(1) + '%');
    }

    return [
      {
        title: 'Chiffre d\'Affaires',
        value: `${totalRevenue.toLocaleString()} DA`,
        icon: BanknotesIcon,
        color: 'from-green-500 to-green-600',
        rawValue: totalRevenue // Store raw value for calculations
      },
      {
        title: 'Valeur du Stock',
        value: `${stockValue.toLocaleString()} DA`,
        icon: ArchiveBoxIcon,
        color: 'from-blue-500 to-blue-600',
        rawValue: stockValue // Store raw value for calculations
      },
      {
        title: 'Rentabilité Globale',
        value: `${profitability >= 0 ? '+' : ''}${profitability.toFixed(1)}%`,
        icon: ChartBarSquareIcon,
        color: 'from-purple-500 to-purple-600',
        rawValue: profitability // Store raw value for calculations
      },
    ];
  }, [documents, products, sales]); // Recalculate when documents, products, or sales change

  // Show loading screen until data is loaded
  if (!isDataLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <motion.div 
          className="flex items-center justify-center min-h-[400px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white/80">Chargement des données en temps réel...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Onglets
  const tabs = [
    { id: 'sales', name: 'Ventes', icon: ChartBarIcon },
    { id: 'inventory', name: 'Inventaire', icon: ArchiveBoxIcon },
    { id: 'purchases', name: 'Achats', icon: TruckIcon },
    { id: 'financial', name: 'Finances', icon: CurrencyDollarIcon },
    { id: 'abc', name: 'Classification ABC', icon: ScaleIcon },
    { id: 'profitability', name: 'Rentabilité', icon: PresentationChartLineIcon },

  ];

  // REMOVED: handleDownloadPDF - Each section now handles its own PDF export

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Rapports</h1>
          <p className="text-white/60">Analysez les données de votre entreprise</p>
        </div>
        {/* REMOVED: General export button - Each section now has its own export */}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Contenu des rapports */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6">
        <Routes>
                      <Route index element={
              <SalesReports 
                documents={documents}
                products={products}
                customers={customers}
                suppliers={suppliers}
              />
            } />
            <Route path="sales" element={
              <SalesReports 
                documents={documents}
                products={products}
                customers={customers}
                suppliers={suppliers}
              />
            } />
          <Route path="inventory" element={
            <InventoryReports 
              products={products}
              documents={documents}
            />
          } />
          <Route path="purchases" element={
            <PurchasesReports 
              documents={documents}
              products={products}
              suppliers={suppliers}
              purchases={purchases}
            />
          } />
          <Route path="financial" element={
            <FinancialReports 
              documents={documents}
              customers={customers}
              suppliers={suppliers}
              sales={sales}
              purchases={purchases}
            />
          } />
          <Route path="abc" element={
            <AbcClassificationReport 
              products={products}
              sales={sales}
              purchases={purchases}
              customers={customers}
              suppliers={suppliers}
              documents={documents}
            />
          } />
          <Route path="profitability" element={
            <ProfitabilityReport 
              documents={documents}
              products={products}
              sales={sales}
              purchases={purchases}
            />
          } />

        </Routes>
      </div>



      {/* Aperçu des rapports - Context-aware overview cards */}
      {(isSalesSection || isInventorySection) && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
          {/* Left card - changes based on section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">
                {isSalesSection ? 'Top Produits Vendus' : 'Top Produits Valorisés'}
              </h3>
            <ChartPieIcon className="h-6 w-6 text-white/60" />
          </div>
          <div className="space-y-3">
              {isSalesSection ? (
                // Sales section - show top sold products
                topProductsFromSales.length > 0 ? (
              topProductsFromSales.map((product, idx) => (
                <div key={product.id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-2 h-8 rounded-full bg-gradient-to-b from-blue-400 to-purple-500 mr-3"></div>
                    <span className="text-white">{product.name}</span>
                  </div>
                  <span className="text-white font-medium">{product.quantity} unités</span>
                </div>
              ))
            ) : (
              <div className="text-center text-white/60 py-8">
                <p>Aucune donnée de vente disponible</p>
                <p className="text-sm">Créez des ventes pour voir les top produits</p>
              </div>
                )
              ) : (
                // Inventory section - show top value products
                inventoryOverviewData.topValueProducts.length > 0 ? (
                  inventoryOverviewData.topValueProducts.map((product, idx) => (
                    <div key={product.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-2 h-8 rounded-full bg-gradient-to-b from-green-400 to-emerald-500 mr-3"></div>
                        <span className="text-white">{product.name}</span>
                      </div>
                      <span className="text-white font-medium">{product.value.toLocaleString()} DA</span>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-white/60 py-8">
                    <p>Aucun produit valorisé</p>
                    <p className="text-sm">Ajoutez des prix aux produits</p>
                  </div>
                )
            )}
          </div>
        </div>
        
          {/* Right card - changes based on section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">
                {isSalesSection ? 'Evolution des Ventes' : 'Stock par Catégorie'}
              </h3>
            <DocumentChartBarIcon className="h-6 w-6 text-white/60" />
          </div>
            <div className="h-60">
              {isSalesSection ? (
                // Sales section - show sales evolution
                salesEvolutionData && salesEvolutionData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={salesEvolutionData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="rgba(255,255,255,0.6)" 
                      fontSize={10}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis stroke="rgba(255,255,255,0.6)" fontSize={10} />
                    <Tooltip 
                      formatter={(value) => [`${value.toLocaleString()} DA`, 'Ventes']}
                      labelStyle={{ color: 'white' }}
                      contentStyle={{ 
                        backgroundColor: 'rgba(30, 41, 59, 0.95)', 
                        borderColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    />
                    <Bar 
                      dataKey="sales" 
                      fill="url(#evolutionGradient)" 
                      name="Ventes"
                      radius={[4, 4, 0, 0]}
                    />
                    <defs>
                      <linearGradient id="evolutionGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" />
                        <stop offset="100%" stopColor="#10B981" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-white/60">
                    <DocumentChartBarIcon className="h-12 w-12 mb-3 opacity-50" />
                    <p className="text-lg font-medium mb-1">Aucune donnée de vente</p>
                    <p className="text-sm">Créez des ventes pour voir l'évolution</p>
                  </div>
                )
              ) : (
                // Inventory section - show stock by category
                inventoryOverviewData.stockByCategory.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={inventoryOverviewData.stockByCategory}
                    margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="rgba(255,255,255,0.6)" 
                      fontSize={10}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis stroke="rgba(255,255,255,0.6)" fontSize={10} />
                    <Tooltip 
                      formatter={(value) => [`${value.toLocaleString()} DA`, 'Valeur Stock']}
                      labelStyle={{ color: 'white' }}
                      contentStyle={{ 
                        backgroundColor: 'rgba(30, 41, 59, 0.95)', 
                        borderColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    />
                    <Bar 
                      dataKey="stockValue" 
                      fill="url(#inventoryGradient)" 
                      name="Valeur Stock"
                      radius={[4, 4, 0, 0]}
                    />
                    <defs>
                      <linearGradient id="inventoryGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" />
                        <stop offset="100%" stopColor="#059669" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-white/60">
                    <ArchiveBoxIcon className="h-12 w-12 mb-3 opacity-50" />
                    <p className="text-lg font-medium mb-1">Aucune donnée de stock</p>
                    <p className="text-sm">Ajoutez des produits pour voir les catégories</p>
                </div>
                )
              )}
          </div>
        </div>
      </motion.div>
      )}
    </div>
  );
};

export default ReportsPage; 